import type Big from 'big.js';
import type { ActivityType, ProductKind } from 'types';
import type { PlanRow, SmartFields } from 'modules/campaign/row';
import type { VendorFactor } from 'modules/supplier/factors/types';
import type { Vendor } from 'modules/supplier/vendor/types';
import type { RTBPlanSettings } from 'modules/rtb/RTBPlanSettings';

export type PlanFactor = VendorFactor & {
  vendor: Vendor;
  vendor_factor: number | null;
};

export type BudgetGroup = {
  id: number;
  title: string;
  budget: string | null;
  config: {
    locked: boolean;
  };
  readonly parent_id: number | null;
  readonly modified_at: number;
  readonly order_index: number;
};

export type PlanStrategyType = 'NAIVE' | 'RTB' | 'OOH';

export const budgetScopes = ['TOTAL', 'MEDIA_INCL_FEE', 'MEDIA_EXCL_FEE'] as const;
export type BudgetScope = (typeof budgetScopes)[number];

export const feeAffinities = ['IMPLICIT', 'EXPLICIT'] as const;
export type FeeAffinity = (typeof budgetScopes)[number];

export const planResolutions = ['DAY', 'WEEK', 'MONTH'] as const;
export type PlanResolution = (typeof planResolutions)[number];

type SpecializedSettingsMap = {
  RTB: RTBPlanSettings;
};

export type PlanSettings<T extends ActivityType = ActivityType> = {
  budget_scope: BudgetScope;
  budget_fee_affinity: FeeAffinity;
} & (T extends keyof SpecializedSettingsMap ? SpecializedSettingsMap[T] : never);

export type PlanResponse = {
  id: number;
  campaign_id: number;
  type: ActivityType;
  // state == "PLANNING" && role.plan
  planning: boolean;
  // BlockState.NEW, BlockState.CANCELED, BlockState.DONE,
  locked: boolean;
  date_from: string;
  date_to: string;

  // meta
  plan_strategy: PlanStrategyType;
  plan_resolution: PlanResolution;

  settings: PlanSettings;

  factors: PlanFactor[];
  groups: BudgetGroup[];
  rows: PlanRow[];
};

export type SmartOption = SmartFields & { index: number };

export type TotalBreakdown = {
  fee: Big;
  media: Big;
  service: Big;
};

export type SummaryFields = {
  clientGrossTotal: Big;
  clientTotal: Big;
  clientTotalBreakdown: TotalBreakdown;
  providerTotal: Big;
  providerTotalBreakdown: TotalBreakdown;
  budgeted: Big;

  months: Record<string, { income: Big; expense: Big }>;
  // FIXME: strategy
  estimatedClicks: Big;
  estimatedImpressions: Big;
  // FIXME: reach
  reachImpressions: Big;
  // sum of reach %, does not make sens without overlap
  reachRate: Big;
  maxReachRate: Big;
};

export type RowSetSummaryFields = SummaryFields & {
  discountRate: Big | undefined;
  revenue: Big;
  margin: Big | undefined;
};

export type EntityKey = string;

export type GridRow = PlanRow & {
  _isGroup: false;
  _key: EntityKey;
  depth: number;
  numChildren: number;
  isExpanded: boolean;
  isSelectable: boolean;
  groupDepth: number;
  groupHeight: number;
};

export type GridGroup = BudgetGroup & {
  _isGroup: true;
  _key: EntityKey;
  summary: RowSetSummaryFields;
  depth: number;
  numChildren: number;
  isExpanded: boolean;
  isSelectable: boolean;
  groupDepth: number;
  groupHeight: number;
};

export type RowOrGroup = GridRow | GridGroup;

// margin, revenue, discount rate, budget diff

export type GridSummaryRow = {
  summary: RowSetSummaryFields & {
    reach?: Big;
    reachUniqueUsers?: Big;
    reachFrequency?: Big;
  };
};

// we don't drop into root all moves are realtive to target

export type NodeType = ProductKind | 'GROUP';

export type ReorderRequest = {
  // 1. Who is moving?
  subjectId: number;
  subjectType: NodeType;

  // 2. Where is it going?
  targetId: number;
  targetType: NodeType;

  // 3. How to position relative to target?
  // 'INSIDE': Make subject a child of target (only valid if target is Group)
  // 'ABOVE': Place subject immediately before target (same parent)
  // 'BELOW': Place subject immediately after target (same parent)
  position: 'INSIDE' | 'ABOVE' | 'BELOW';
};
